// src/pages/PlatformBillingCanceled.js
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}
function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

export default function PlatformBillingCanceled() {
  const nav = useNavigate();
  const auth = useAuth();
  const [params] = useSearchParams();

  const token = safeString(auth?.token).trim();
  const isAuthed = !!token;

  const platformRole = normalizePlatformRole(
    auth?.user?.platformRole ?? auth?.platformRole ?? ""
  );
  const hasPlatformAccess = !!platformRole;

  // Optional: /platform/billing/canceled?reason=checkout|subscription
  const reason = safeString(params.get("reason")).toLowerCase().trim();

  const title =
    reason === "subscription"
      ? "Subscription canceled"
      : reason === "checkout"
      ? "Checkout canceled"
      : "Billing update";

  const subtitle =
    reason === "subscription"
      ? "Your subscription has been set to cancel (or has been canceled)."
      : reason === "checkout"
      ? "No payment was processed."
      : "No payment was processed, or your subscription was updated.";

  const bullets =
    reason === "subscription"
      ? [
          "If cancellation was set for end-of-period, access continues until the current billing period ends.",
          "You can restart a subscription at any time from Billing.",
          "If this was a mistake, start a new subscription and it will reactivate access.",
        ]
      : [
          "You can try checkout again from Billing.",
          "If you hit an error, check that your Stripe products/prices are correct.",
          "You can always switch plans later — nothing is locked in.",
        ];

  // Route-authoritative platform detection (works even if mode flag is stale)
  const pathIsPlatform =
    typeof window !== "undefined" &&
    safeString(window.location.pathname).startsWith("/platform");

  const mode = safeString(auth?.mode).toLowerCase().trim();
  const inPlatformMode = pathIsPlatform || mode === "platform" || !!auth?.isPlatform;

  // Guards
  if (!isAuthed) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">{title}</h1>
            <p className="ft-subtitle">Please sign in to continue.</p>
          </div>
          <div className="ft-header__actions">
            <button className="ft-btn ft-btn--primary" type="button" onClick={() => nav("/login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPlatformAccess) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">{title}</h1>
            <p className="ft-subtitle">This account does not have Platform access.</p>
          </div>
          <div className="ft-header__actions">
            <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/dashboard")}>
              Go to Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!inPlatformMode) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">{title}</h1>
            <p className="ft-subtitle">Switch to Platform mode using the sidebar toggle.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ft-page">
      <div className="ft-header">
        <div className="ft-header__text">
          <h1 className="ft-title">{title}</h1>
          <p className="ft-subtitle">{subtitle}</p>
        </div>

        <div className="ft-header__actions">
          <button className="ft-btn ft-btn--primary" type="button" onClick={() => nav("/platform/billing")}>
            Go to Billing
          </button>
          <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/platform")}>
            Return to Platform
          </button>
        </div>
      </div>

      <div className="ft-card">
        <div className="ft-card__header">
          <div className="ft-card__headerTitle">Next steps</div>
          <div className="ft-card__headerSub">You’re in control.</div>
        </div>
        <div className="ft-card__body">
          <ul className="ft-listPlain">
            {bullets.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>

          <div className="ft-hint" style={{ marginTop: 10 }}>
            Tip: You can drive copy via <code>?reason=checkout</code> / <code>?reason=subscription</code>.
          </div>
        </div>
      </div>
    </div>
  );
}